import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Sparkles, Calendar, Library, Compass } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useEventModules } from '../hooks/useEventModules';
import {
  buildWelcomeFeatureSteps,
  type WelcomeFeatureStepKind,
} from '../utils/eventFeatureCopy';

const STEP_ICONS: Record<WelcomeFeatureStepKind, typeof Sparkles> = {
  welcome: Sparkles,
  agenda: Calendar,
  ai: Sparkles,
  library: Library,
  explore: Compass,
};

/**
 * Welcome guide shown to first-time users
 */
export function WelcomeGuide() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const { currentEventId } = useApp();
  const { modules, isReady } = useEventModules();
  const steps = buildWelcomeFeatureSteps(modules);

  useEffect(() => {
    setCurrentStep((step) => Math.min(step, steps.length - 1));
  }, [steps.length]);

  useEffect(() => {
    // Check if user has seen the guide
    const hasSeenGuide = localStorage.getItem('has_seen_welcome_guide');
    if (!hasSeenGuide && currentEventId && isReady) {
      setIsVisible(true);
    }
  }, [currentEventId, isReady]);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('has_seen_welcome_guide', 'true');
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-md rounded-2xl p-6 border"
          style={{ backgroundColor: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 transition-colors active:opacity-70"
            style={{ color: 'var(--app-text-subtle)' }}
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="flex flex-col items-center text-center mt-4">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <div className="mb-6">{(() => {
                const Icon = STEP_ICONS[steps[currentStep].kind];
                return <Icon className="w-12 h-12" style={{ color: 'var(--app-accent)' }} />;
              })()}</div>
              <h2 className="text-2xl mb-3" style={{ color: 'var(--app-text)' }}>
                {steps[currentStep].title}
              </h2>
              <p className="text-sm leading-relaxed mb-8" style={{ color: 'var(--app-text-muted)' }}>
                {steps[currentStep].description}
              </p>
            </motion.div>

            {/* Progress indicators */}
            <div className="flex gap-2 mb-6">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className="h-1 rounded-full transition-all"
                  style={{
                    width: index === currentStep ? '32px' : '4px',
                    backgroundColor: index === currentStep ? 'var(--app-accent-solid)' : 'var(--app-muted)',
                  }}
                />
              ))}
            </div>

            {/* Buttons */}
            <div className="flex gap-3 w-full">
              {currentStep > 0 && (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="flex-1 px-6 py-3 rounded-xl transition-colors text-sm"
                  style={{ backgroundColor: 'var(--app-muted)', color: 'var(--app-text)' }}
                >
                  Back
                </button>
              )}
              <button
                onClick={handleNext}
                className="flex-1 px-6 py-3 text-white rounded-xl transition-opacity hover:opacity-90 text-sm"
                style={{
                  background: 'linear-gradient(135deg, var(--app-accent-solid), var(--app-accent))',
                  boxShadow: '0 6px 16px var(--app-accent-glow)',
                }}
              >
                {currentStep === steps.length - 1 ? "Let's Go!" : 'Next'}
              </button>
            </div>

            <button
              onClick={handleClose}
              className="mt-4 text-sm transition-colors active:opacity-70"
              style={{ color: 'var(--app-text-subtle)' }}
            >
              Skip tutorial
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
